import "./App.css";
import { RouterProvider } from "react-router-dom";
import { router } from "./routes/router";
import { useAppDispatch } from "@quest/hooks";
import { setTenantInfo } from "@quest/slice/tenant/tenant-slice";
import { useKeycloakClient } from "@quest/hooks";
import Loader from "@quest/ui/components/loader";
import { getClientIdFromSubdomain } from "./libs/utils/client-slug";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useGetMaintenanceStatus } from "@quest/hooks/maintenance";
import MaintenancePage from "@quest/shared/maintenance/maintenancePage";
import { setMaintenanceMode } from "@quest/slice/maintenance/maintenance.slice";
import { AlertCircle, RefreshCw, School } from "lucide-react";

function App() {
  //util function to get the client id from the subdomain
  const clientDetails = getClientIdFromSubdomain();

  const { data, isLoading, isFetched, error } =
    useKeycloakClient(clientDetails);

  const { data: maintenanceStatus, isLoading: isMaintenanceLoading } =
    useGetMaintenanceStatus();

  const { t: tValidations } = useTranslation("Validations");

  const dispatch = useAppDispatch();

  // in your app's entry point, e.g. main.tsx
  // window.addEventListener("vite:preloadError", () => {
  //   window.location.reload();
  // });

  if (data && isFetched) {
    // storing the tenant client id, logo, client slug name which is required for api calls
    dispatch(setTenantInfo(data?.data));
  }

  if (maintenanceStatus?.isMaintenanceMode) {
    dispatch(setMaintenanceMode(maintenanceStatus?.isMaintenanceMode));
  }

  if (maintenanceStatus?.isMaintenanceMode) {
    return <MaintenancePage />;
  }

  if (error instanceof AxiosError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>

          <h1 className="text-xl font-semibold text-gray-900">
            {tValidations(error?.response?.data?.message) ??
              "An unexpected error occurred. Please try again later."}
          </h1>

          <p className="mt-3 text-sm text-gray-600">
            {tValidations("somethingWentWrong")}
          </p>

          <button
            onClick={() => window.location.reload()}
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-red-700"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (data?.data?.status === "inactive") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-xl border border-amber-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
            <School className="h-8 w-8 text-amber-600" />
          </div>

          <h1 className="text-xl font-semibold text-gray-900">
            School Deactivated
          </h1>

          <p className="mt-3 text-sm leading-6 text-gray-600">
            Your school's account has been deactivated. Please contact your
            administrator for assistance or to reactivate your account.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading || isMaintenanceLoading) {
    return <Loader />;
  }

  return (
    <>
      {maintenanceStatus?.isMaintenanceMode ? (
        <MaintenancePage />
      ) : (
        <RouterProvider router={router} />
      )}
    </>
  );
}

export default App;
